import { useEffect, useState } from "react";
import api, { API } from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBButton, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Upload, Trash2, X } from "lucide-react";

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const token = localStorage.getItem("familyos_token");

  const load = async () => {
    const { data } = await api.get("/api/photos");
    setPhotos(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setUploading(true);
    try {
      await api.post("/api/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded!");
      setFile(null);
      setCaption("");
      setOpen(false);
      load();
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete photo?")) return;
    await api.delete(`/api/photos/${id}`);
    load();
  };

  return (
    <div data-testid="photos-page">
      <PageHeader
        title="Photo Album"
        subtitle="Family memories, all in one place."
        color="#A8BFD1"
        action={<NBButton testId="upload-photo-btn" color="#A8BFD1" onClick={() => setOpen(true)}><Upload strokeWidth={3} className="w-4 h-4 inline mr-1" />Upload</NBButton>}
      />

      {photos.length === 0 ? (
        <EmptyState title="No photos yet" subtitle="Upload your first family photo." action={<NBButton onClick={() => setOpen(true)}>Upload</NBButton>} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="nb-card overflow-hidden !p-0 nb-card-hover relative group" data-testid={`photo-${p.id}`}>
              <button onClick={() => setPreview(p)} className="block w-full">
                <div className="aspect-square bg-[#E8B96B]">
                  <img
                    src={`${API}/photos/${p.id}/download?auth=${token}`}
                    alt={p.caption || "Family"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </button>
              {p.caption && <p className="text-xs font-bold p-2 border-t-2 border-black bg-white truncate">{p.caption}</p>}
              <button
                onClick={() => remove(p.id)}
                className="absolute top-2 right-2 nb-btn bg-[#E9B5A0] p-1.5 opacity-0 group-hover:opacity-100"
                data-testid={`delete-photo-${p.id}`}
              >
                <Trash2 strokeWidth={2.5} className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Upload Photo</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <input
                type="file"
                accept="image/*"
                required
                data-testid="photo-file-input"
                onChange={(e) => setFile(e.target.files?.[0])}
                className="nb-input w-full file:mr-3 file:rounded file:border-2 file:border-black file:bg-[#E8B96B] file:px-3 file:py-1 file:font-bold"
              />
              <NBInput testId="photo-caption" placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} />
              <NBButton testId="submit-photo" type="submit" disabled={uploading} color="#A8BFD1" className="w-full">
                {uploading ? "Uploading..." : "Upload"}
              </NBButton>
            </form>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <div className="max-w-4xl w-full">
            <img src={`${API}/photos/${preview.id}/download?auth=${token}`} alt={preview.caption} className="w-full max-h-[85vh] object-contain border-2 border-black nb-shadow-xl bg-white" />
            {preview.caption && <p className="text-white font-bold mt-3 text-center">{preview.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
